import React from "react";
import Navbar from "../../components/Navbar";
import { Check, X, ArrowRight, Zap, DollarSign, Gauge, RefreshCw } from "lucide-react";

const comparisonData = {
  qdrant: {
    name: "Qdrant",
    tagline: "Filtering-first open-source vector database",
    pros: [
      "Strong metadata filtering and payload support",
      "Open-source with Rust-based performance",
      "Easy self-hosting and cloud options",
      "Good balance between performance and usability",
      "Fast setup with REST and gRPC APIs"
    ],
    cons: [
      "Less optimised for billion-scale datasets",
      "Smaller ecosystem compared to Milvus",
      "Limited built-in AI modules",
      "Scaling requires more manual tuning",
      "Enterprise tooling still evolving"
    ],
    pricing: {
      tier: "Free / Moderate",
      description: "Free for self-hosted deployments. Managed Qdrant Cloud offers predictable, mid-range pricing.",
      note: "Best for: Teams needing strong filtering with manageable scale"
    },
    performance: {
      latency: "Low latency for small–medium datasets",
      throughput: "Well-optimised for filtered vector search"
    }
  },

  milvus: {
    name: "Milvus",
    tagline: "Distributed vector database built for massive scale",
    pros: [
      "Designed for billion-scale vector search",
      "Highly scalable distributed architecture",
      "Strong performance for large AI workloads",
      "Cloud-native with Kubernetes support",
      "Backed by a mature open-source ecosystem"
    ],
    cons: [
      "Operationally complex to manage",
      "Higher infrastructure requirements",
      "Steeper learning curve",
      "Overkill for small or medium projects",
      "Requires tuning for optimal performance"
    ],
    pricing: {
      tier: "Infrastructure-Based",
      description: "Open-source and free to use, but infrastructure and operational costs can be high at scale.",
      note: "Best for: Large-scale AI platforms and enterprise workloads"
    },
    performance: {
      latency: "Optimised for large-scale distributed queries",
      throughput: "Handles massive concurrent vector workloads"
    }
  }
};

const migrationDetails = {
  difficulty: "Medium",
  timeEstimate: "2–4 hours for 1M vectors",
  considerations: [
    "Schema and collection mapping differences",
    "Index type and distance metric alignment",
    "Metadata and payload compatibility",
    "Cluster resource planning for Milvus"
  ],
  howWeHelp: [
    "Automated Qdrant-to-Milvus schema translation",
    "Index and metric compatibility validation",
    "Batch ingestion optimization for Milvus",
    "Data integrity and embedding verification",
    "Performance tuning recommendations post-migration"
  ]
};

const ComparisonSection = ({ title, icon: Icon, children }) => (
  <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 mb-8">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
        <Icon className="w-5 h-5 text-black" />
      </div>
      <h2 className="text-2xl font-bold text-white">{title}</h2>
    </div>
    {children}
  </div>
);

const ProConList = ({ items, isPositive }) => (
  <ul className="space-y-3">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-3">
        <div
          className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
            isPositive ? "bg-white/20" : "bg-gray-700"
          }`}
        >
          {isPositive ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <X className="w-3 h-3 text-red-500" />
          )}
        </div>
        <span className="text-gray-300 text-sm">{item}</span>
      </li>
    ))}
  </ul>
);

const DatabaseCard = ({ data }) => (
  <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
    <h3 className="text-2xl font-bold text-white mb-2">{data.name}</h3>
    <p className="text-gray-400 text-sm mb-6">{data.tagline}</p>

    <div className="space-y-6">
      <div>
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Check className="text-green-500 w-4 h-4" />
          Advantages
        </h4>
        <ProConList items={data.pros} isPositive />
      </div>

      <div>
        <h4 className="text-gray-400 font-semibold mb-3 flex items-center gap-2">
          <X className="text-red-500 w-4 h-4" />
          Limitations
        </h4>
        <ProConList items={data.cons} />
      </div>
    </div>
  </div>
);

const PricingCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-white">{name}</h3>
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-800 text-gray-300 border border-gray-700">
        {data.tier}
      </span>
    </div>
    <p className="text-gray-300 text-sm mb-3">{data.description}</p>
    <p className="text-gray-500 text-xs italic">{data.note}</p>
  </div>
);

const PerformanceCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-white">{name}</h3>
    </div>
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Query Latency:</span>
        <span className="text-gray-300">{data.latency}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Throughput:</span>
        <span className="text-gray-300">{data.throughput}</span>
      </div>
    </div>
  </div>
);

export default function QdrantVsMilvus() {
  return (
    <div className="bg-black min-h-screen px-6 py-10">
      <Navbar />
      <div className="max-w-6xl py-28 mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">
            Qdrant vs Milvus: Comparison 2026
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto">
            Filtering-first flexibility vs massive-scale vector search. Compare
            performance, scalability, operational complexity, and migration
            considerations for modern AI systems.
          </p>
        </div>

        {/* Side-by-Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <DatabaseCard data={comparisonData.qdrant} />
          <DatabaseCard data={comparisonData.milvus} />
        </div>

        {/* Pricing */}
        <ComparisonSection title="Pricing Comparison" icon={DollarSign}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PricingCard data={comparisonData.qdrant.pricing} name="Qdrant" />
            <PricingCard data={comparisonData.milvus.pricing} name="Milvus" />
          </div>
        </ComparisonSection>

        {/* Performance */}
        <ComparisonSection title="Performance Metrics" icon={Gauge}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PerformanceCard data={comparisonData.qdrant.performance} name="Qdrant" />
            <PerformanceCard data={comparisonData.milvus.performance} name="Milvus" />
          </div>
        </ComparisonSection>

        {/* Migration */}
        <ComparisonSection title="Migration from Qdrant to Milvus" icon={RefreshCw}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Migration Complexity</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400 text-sm">Difficulty Level:</span>
                  <span className="text-gray-300 font-semibold">
                    {migrationDetails.difficulty}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400 text-sm">Estimated Time:</span>
                  <span className="text-gray-300 text-sm">
                    {migrationDetails.timeEstimate}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-4">
                Key Considerations
              </h3>
              <ul className="space-y-2">
                {migrationDetails.considerations.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-gray-400 mt-0.5" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </ComparisonSection>

        {/* Final Recommendation */}
        <div className="mt-16 bg-gray-900 border border-gray-800 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">
            Which Should You Choose?
          </h2>
          <div className="space-y-4 text-gray-300">
            <p>
              <strong className="text-white">Choose Qdrant if:</strong> You need
              strong filtering, manageable scale, and a simpler operational
              setup for AI applications.
            </p>
            <p>
              <strong className="text-white">Choose Milvus if:</strong> You’re
              operating at massive scale, handling billions of vectors, and can
              support distributed infrastructure.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
